/**
 * KIT BUILDER LOGIC v7.1
 * - Shock order iz tagova:  Order_<GROUP> -- SHOCKNAME_INDEX
 * - Load order iz tagova:   LoadOrder_Label_Index
 * - Auto-select prvog shocka + lifta + loada
 */

export function initKitBuilder() {
  const spinner = document.querySelector('#loading-spinner');
  if (spinner) spinner.classList.remove('hidden');

  const root = document.querySelector('[data-kit-builder-root]');
  if (!root) return;

  const dataEl = document.querySelector('#kit-builder-data');
  if (!dataEl) return;

  const productData = JSON.parse(dataEl.textContent);
  const { tags, metafields } = productData;

  // 1) Složi strukturu iz ## tagova
  const structure = buildStructure(tags);

  // 2) Parsiraj order tagove (shock + load)
  const { shockOrder, loadOrder } = parseOrdering(tags);

  // 3) Primijeni load ordering unutar structure (sortiraj loads po LoadOrder_*)
  applyLoadOrder(structure, loadOrder);

  // 4) Render layouta s poretkom shockova
  renderFullLayout(root, structure, metafields, shockOrder);
  waitAndMoveKitImageWrapper(root);

  // 5) Poveži interakcije
  attachInteractions(root, structure);

  // 6) Auto-select prvog shocka (+ automatski povlači lift + load)
// 6) Auto-select prvog shocka (+ automatski povlači lift + load)
autoSelectFirst(root);

// ⏳ sačekaj da se završe svi click handleri
setTimeout(() => {
  renderActiveVariantImage();
}, 0);

  // Sakrij loader nakon što je sve spremno
  setTimeout(() => {
    if (spinner) spinner.classList.add('hidden');
  }, 1000);
}

/* ---------- Parsiranje Order_ i LoadOrder_ tagova ---------- */
/**
 * Primjer:
 *  - "Order_PRO -- MT64_2"            => shockOrder["MT64"] = 2
 *  - "Order_PREMIUM -- NITROCHARGER+_0" => shockOrder["NITROCHARGER+"] = 0
 *  - "LoadOrder_Stock_1"             => loadOrder["STOCK"] = 1
 *  - "LoadOrder_Standard_2"          => loadOrder["STANDARD"] = 2
 */
function parseOrdering(tags) {
  const shockOrder = {};
  const loadOrder = {};

  tags.forEach((tag) => {
    if (tag.startsWith('Order_')) {
      // npr: "Order_PREMIUM -- NITROCHARGER+_0"
      const withoutPrefix = tag.replace(/^Order_/, '');
      const [left, indexStr] = withoutPrefix.split('_');
      const shockName = left.split('--').pop().trim(); // "MT64", "NITROCHARGER+", "BP51"
      const idx = parseInt(indexStr, 10);
      if (!Number.isNaN(idx)) {
        shockOrder[shockName.toUpperCase()] = idx;
      }
    } else if (tag.startsWith('LoadOrder_')) {
      // npr: "LoadOrder_Standard_2"
      const withoutPrefix = tag.replace(/^LoadOrder_/, '');
      const [label, indexStr] = withoutPrefix.split('_');
      const idx = parseInt(indexStr, 10);
      if (!Number.isNaN(idx)) {
        loadOrder[label.toUpperCase()] = idx;
      }
    }
  });

  return { shockOrder, loadOrder };
}

/* ---------- Build structured data iz glavnih ## tagova ---------- */

function buildStructure(tags) {
  const structure = {};

  tags.forEach((tag) => {
    if (!tag.includes('##')) return;

    const [shockPart, metaPart] = tag.split('##');
    const shock = shockPart.split('|')[0].trim(); // npr "PRO -- MT64"
    const lift = shockPart.includes('&&')
      ? shockPart.split('&&')[0].split('|').pop().trim()
      : null;
    const load = tag.includes('&&')
      ? tag.split('&&').pop().split('##')[0].trim()
      : null;

    const variantId = metaPart?.split('**')[0]?.trim();
    const afterImg = metaPart?.split('**')[1] || '';
    const handle = afterImg.includes('^^') ? afterImg.split('^^').pop().trim() : null;
    const imgUrlRaw = afterImg.includes('^^') ? afterImg.split('^^')[0].trim() : afterImg.trim();
    const imgUrl = imgUrlRaw.startsWith('http')
      ? imgUrlRaw
      : imgUrlRaw
      ? `https://cdn.shopify.com/s/files/1/0559/6944/8111/files/${imgUrlRaw}`
      : null;

    if (!structure[shock]) structure[shock] = { lifts: {} };

    if (lift) {
      if (!structure[shock].lifts[lift]) {
        structure[shock].lifts[lift] = { loads: [] };
      }
      if (load) {
        structure[shock].lifts[lift].loads.push({
          load,
          variantId,
          handle,
          imgUrl,
        });
      }
    }
  });

  return structure;
}

/* ---------- Primijeni LoadOrder_* sort na structure.lifts[*].loads ---------- */

function applyLoadOrder(structure, loadOrder) {
  Object.keys(structure).forEach((shockKey) => {
    const shockObj = structure[shockKey];
    Object.keys(shockObj.lifts).forEach((liftKey) => {
      const liftObj = shockObj.lifts[liftKey];
      liftObj.loads.sort((a, b) => {
        const aIdx = loadOrder[a.load.toUpperCase()] ?? 999;
        const bIdx = loadOrder[b.load.toUpperCase()] ?? 999;
        if (aIdx !== bIdx) return aIdx - bIdx;
        return a.load.localeCompare(b.load);
      });
    });
  });
}


function normalizeShock(val) {
  return val?.replace(/\s+/g, ' ').trim().toUpperCase();
}


/* ---------- Helper za sortiranje shock ključeva po shockOrder mapi ---------- */

function getSortedShockKeys(structure, shockOrder) {
  const keys = Object.keys(structure);
  return keys.sort((a, b) => {
    // a: "PRO -- MT64", b: "PREMIUM -- NITROCHARGER+"
    const aName = a.split('--').pop().trim().toUpperCase(); // "MT64"
    const bName = b.split('--').pop().trim().toUpperCase(); // "NITROCHARGER+"
    const aIdx = shockOrder[aName] ?? 999;
    const bIdx = shockOrder[bName] ?? 999;
    if (aIdx !== bIdx) return aIdx - bIdx;
    return aName.localeCompare(bName);
  });
}

/* ---------- Helper za sortiranje lift ključeva: 'Leveling' prvo ---------- */
function getSortedLiftKeysForShock(structure, shockKey) {
  const shockObj = structure[shockKey];
  if (!shockObj || !shockObj.lifts) return [];

  return Object.keys(shockObj.lifts).sort((a, b) => {
    const aLev = /LEVELING/i.test(a);
    const bLev = /LEVELING/i.test(b);

    // 1) Leveling uvijek prvi
    if (aLev && !bLev) return -1;
    if (!aLev && bLev) return 1;

    // 2) Numeričko poređenje (“2” < “2.5” < “3”)
    const numA = parseFloat(a.replace(/[^0-9.]/g, '')) || 0;
    const numB = parseFloat(b.replace(/[^0-9.]/g, '')) || 0;
    return numA - numB;
  });
}


/* ---------- Render layout ---------- */
function waitAndMoveKitImageWrapper(root) {
  const imageWrapper = document.querySelector('.kit-image-wrapper');
  if (!imageWrapper) return;

  const tryMove = () => {
    const loadGroup = root.querySelector('[data-load-group]');
    if (!loadGroup) return false;

    loadGroup.insertAdjacentElement('afterend', imageWrapper);
    return true;
  };

  // pokušaj odmah
  if (tryMove()) return;

  // ako ne uspije, čekaj DOM promjene
  const observer = new MutationObserver(() => {
    if (tryMove()) {
      observer.disconnect();
    }
  });

  observer.observe(root, {
    childList: true,
    subtree: true,
  });
}



function renderFullLayout(root, structure, metafields, shockOrder) {
  root.innerHTML = `
    <h3>${metafields.option1 || 'SELECT SHOCK TYPE'}</h3>
    <div data-shock-group></div>

    <h3>${metafields.option2 || 'SELECT YOUR LIFT'}</h3>
    <div data-lift-group></div>

    <h3>
      ${metafields.option3 || 'SELECT YOUR LOAD'}
      <a href="#" class="learn-more" data-popup-target="#custom-popup-constant_load">LEARN MORE</a>
    </h3>
    <div data-load-group></div>

    <!-- ✅ Image area -->
    <div class="kit-image-wrapper mt-8 mb-1 variant-images-wrapper">
      <img data-kit-image src="" alt="" style="display:none;max-width:600px;width: 100%; margin:0 auto;border-radius:12px;" />
      <a href="#" data-variant-details class="kit-builder__variant"></a>
      </div>

    <!-- ✅ dynamic SVG icons container -->
    <div class="lift-kit__icons flex justify-center items-center gap-6 mt-6"></div>

    <div class="" data-product-info></div>
  `;

  const shockGroup = root.querySelector('[data-shock-group]');
  const liftGroup = root.querySelector('[data-lift-group]');
  const loadGroup = root.querySelector('[data-load-group]');

  // 🔹 sortirani shock ključevi prema Order_ tagovima
  const sortedShocks = getSortedShockKeys(structure, shockOrder);

  // SHOCK buttons
  sortedShocks.forEach((shock) => {
    const btn = document.createElement('button');
    btn.className = 'shock-btn';
    const cleaned = shock.replace(/-/g, ' ');

    btn.innerHTML = `
      ${cleaned.split(' ')[0].toUpperCase()}
      <hr>
      ${cleaned.split(' ').slice(1).join(' ').toUpperCase()}
    `;
    btn.dataset.shock = shock;
    shockGroup.appendChild(btn);
  });

  // LIFT buttons (po shocku)
sortedShocks.forEach((shock) => {
  const lifts = getSortedLiftKeysForShock(structure, shock);
  lifts.forEach((lift) => {
    const btn = document.createElement('button');
    btn.className = 'lift-btn hidden';
    btn.dataset.shock = shock;
    btn.dataset.lift = lift;
    btn.textContent = lift;
    liftGroup.appendChild(btn);
  });
});


  // LOAD buttons (koriste već sortirani liftObj.loads iz applyLoadOrder)
  sortedShocks.forEach((shock) => {
    const lifts = Object.keys(structure[shock].lifts);
    lifts.forEach((lift) => {
      const loads = structure[shock].lifts[lift].loads;
      loads.forEach((l) => {
        const btn = document.createElement('button');
        btn.className = 'load-btn hidden';
        btn.dataset.shock = shock;
        btn.dataset.lift = lift;
        btn.dataset.handle = l.handle;
        btn.dataset.variantId = l.variantId;
        if (l.imgUrl) btn.dataset.imgUrl = l.imgUrl;
        btn.textContent = l.load;
        loadGroup.appendChild(btn);
      });
    });
  });
}

/* ---------- Interactions ---------- */
function renderVariantDetails(variantId, options = {}) {
  const { handle, shockKey, imgUrl } = options;
  const target = document.querySelector('[data-variant-details]');
  if (!target) return false;

  // Always clear old rendered image so lift/load toggles don't keep stale image.
  target.innerHTML = '';
  target.removeAttribute('href');

  const links = [...document.querySelectorAll('.kit-variant-image-link')];
  let sourceLink = null;

  if (variantId) {
    sourceLink = links.find((el) => el.dataset.variantLink === String(variantId));
  }

  if (!sourceLink && handle) {
    sourceLink = links.find((el) => el.dataset.shockHandle === String(handle));
  }

  if (!sourceLink && shockKey) {
    const normalized = normalizeShock(shockKey);
    sourceLink = links.find((el) => normalizeShock(el.dataset.shock) === normalized);
  }

  if (sourceLink) {
    const img = sourceLink.querySelector('img');
    if (!img) return false;

    target.href = sourceLink.href;
    const clonedImg = img.cloneNode(true);
    clonedImg.style.display = 'block';
    target.appendChild(clonedImg);
    return true;
  }

  if (imgUrl) {
    const fallbackImg = document.createElement('img');
    fallbackImg.src = imgUrl;
    fallbackImg.alt = handle || shockKey || 'Kit image';
    fallbackImg.loading = 'lazy';
    fallbackImg.decoding = 'async';
    fallbackImg.style.display = 'block';
    fallbackImg.style.maxWidth = '600px';
    fallbackImg.style.width = '100%';
    fallbackImg.style.borderRadius = '12px';
    target.appendChild(fallbackImg);

    if (handle) target.href = `/products/${handle}`;
    return true;
  }

  return false;
}

function renderShockImage(shockKey) {
  const target = document.querySelector('[data-variant-details]');
  if (!target) return;

  const shockName = normalizeShock(shockKey);

  const link = [...document.querySelectorAll('.kit-variant-image-link')]
    .find(el => normalizeShock(el.dataset.shock) === shockName);

  if (!link) return;

  const img = link.querySelector('img');
  if (!img) return;

  target.innerHTML = '';
  target.href = link.href;

  const cloned = img.cloneNode(true);
  cloned.style.display = 'block';

  target.appendChild(cloned);
}


function updateVariantImageFromLoad(btn) {
  if (!btn) return;

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  renderVariantDetails(variantId);
}

function updateImageFromButton(btn) {
  if (!btn) return;
  const variantId = btn.dataset.variantId;
  if (!variantId) return;
  renderVariantDetails(variantId);
}


function attachInteractions(root, structure) {
  const shockBtns = root.querySelectorAll('.shock-btn');
  const liftBtns = root.querySelectorAll('.lift-btn');
  const loadBtns = root.querySelectorAll('.load-btn');
  const infoBox = root.querySelector('[data-product-info]');

  shockBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const shock = btn.dataset.shock;
  
      shockBtns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
  
      liftBtns.forEach((lift) => {
        lift.classList.add('hidden');
        if (lift.dataset.shock === shock) lift.classList.remove('hidden');
      });
  
      loadBtns.forEach((load) => load.classList.add('hidden'));
      infoBox.innerHTML = '';
  
      // ✅ ODMAH fallback slika
      renderShockImage(shock);
  
      // auto-select lift
      const liftKeys = getSortedLiftKeysForShock(structure, shock);
      const firstLift = liftKeys[0];
      if (firstLift) {
        const liftBtn = [...liftBtns].find(
          (b) => b.dataset.shock === shock && b.dataset.lift === firstLift
        );
        if (liftBtn) {
          liftBtns.forEach((b) => b.classList.remove('active'));
          liftBtn.classList.add('active');
          liftBtn.click();
        }
      }
    });
  });
  


 // LIFT click
liftBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    const shock = btn.dataset.shock;
    const lift = btn.dataset.lift;

    // 1️⃣ Active stanje lifta
    liftBtns.forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');

    // 2️⃣ ⛔️ KLJUČNO: odmah resetuj ACTIVE load (sprječava krivu sliku)
    loadBtns.forEach((b) => b.classList.remove('active'));

    // 3️⃣ Prikaži loadove samo za ovaj shock + lift
    loadBtns.forEach((load) => {
      load.classList.add('hidden');
      if (load.dataset.shock === shock && load.dataset.lift === lift) {
        load.classList.remove('hidden');
      }
    });

    // 4️⃣ Reset info boxa
    infoBox.innerHTML = '';

    // 5️⃣ Auto-select PRVOG loada za taj lift
    setTimeout(() => {
      const visibleLoads = [...loadBtns].filter(
        (l) =>
          !l.classList.contains('hidden') &&
          l.dataset.shock === shock &&
          l.dataset.lift === lift
      );

      if (!visibleLoads.length) return;

      const firstLoad = visibleLoads[0];
      firstLoad.classList.add('active');

      // 🔹 pusti LOAD handler da uradi sve ostalo (slika, cijena, ikonice)
      firstLoad.click();
    }, 0);
  });
});

  // LOAD click
  loadBtns.forEach((btn) => {
    btn.addEventListener('click', async () => {
      loadBtns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');

      const img = root.querySelector('[data-kit-image]');
      if (btn.dataset.imgUrl) {
        img.src = btn.dataset.imgUrl;
        img.style.display = 'block';
      } else {
        img.style.display = 'none';
      }
      
      const handle = btn.dataset.handle;
      const variantId = btn.dataset.variantId;
      renderVariantDetails(variantId, {
        handle,
        shockKey: btn.dataset.shock,
        imgUrl: btn.dataset.imgUrl
      });
      if (!handle) return;

      try {
        infoBox.classList.add('loading');

        const res = await fetch(`/products/${handle}.js`);
        const data = await res.json();

        // 🔹 Pročitaj preloader JSON za assembly metafield
        let hasAssembly = false;
        const metaEl = document.querySelector('#kit-builder-products-meta');
        let productMeta = {};
        if (metaEl) {
          try {
            productMeta = JSON.parse(metaEl.textContent);
          } catch (e) {
            console.warn('Failed to parse metafields JSON:', e);
          }
        }

        const currentMeta = productMeta[handle];
        hasAssembly = Boolean(
          currentMeta?.front_shocks_assembly &&
            currentMeta.front_shocks_assembly !== 'null' &&
            currentMeta.front_shocks_assembly !== ''
        );

        // 🔹 SVG ikonice
        let iconsHTML = `
 <svg class="free-shipping" width="164" height="26" viewBox="0 0 164 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.84995 16.2C6.54994 16.2 6.84995 15.6 6.64995 15.1C6.54995 14.7 6.24995 14.6 5.84995 14.6C4.74995 14.6 3.54995 14.6 2.44995 14.6C1.94995 14.6 1.64996 14.4 1.54996 13.9C1.44996 13.4 1.84995 12.8 2.34995 12.8C3.24995 12.8 4.14996 12.8 5.04996 12.8C5.64996 12.8 5.94994 12.4 5.84995 11.8C5.74995 11.4 5.44996 11.1 5.04996 11.1C3.74996 11.1 2.34996 11.1 1.04996 11.1C0.949957 11.6 0.949951 12.1 0.949951 12.6C0.949951 13.8 1.14995 15 1.44995 16.1C2.94995 16.2 4.34995 16.2 5.84995 16.2Z" fill="#F27E00"></path>
            <path d="M26.45 11.4C25.75 5 20.35 0 13.75 0C7.84995 0 2.84995 4 1.44995 9.5C2.44995 9.5 3.44996 9.5 4.54996 9.5C4.84996 9.5 5.14995 9.5 5.44995 9.5C5.84995 9.4 6.24996 9.6 6.54996 9.8C8.64996 11 10.85 12.3 13.05 13.5C13.15 13.6 13.25 13.6 13.25 13.8C13.25 16.5 13.25 19.2 13.25 21.9C13.25 22 13.25 22 13.25 22.1C12.85 21.9 12.55 21.7 12.25 21.6C10.25 20.4 8.24995 19.2 6.14995 18.1C6.04995 18 5.84995 18 5.74995 18C4.54995 18 3.34995 18 2.14995 18C4.14995 22.4 8.54995 25.5 13.75 25.5C20.35 25.5 25.75 20.5 26.45 14.1C26.55 13.7 26.55 13.3 26.55 12.8C26.45 12.3 26.45 11.8 26.45 11.4ZM13.8499 12.3C11.5499 10.9 9.14995 9.6 6.74995 8.2C6.64995 8.2 6.64995 8.1 6.64995 8.1C6.74995 8 6.84995 8 6.84995 7.9C7.94994 7.3 8.94996 6.7 10.05 6.1C10.15 6.1 10.1499 6 10.1499 6C10.2499 5.9 10.25 5.8 10.25 5.8C10.25 5.7 10.15 5.7 10.05 5.7C9.94996 5.7 9.84995 5.7 9.84995 5.7C9.24994 5.7 8.74995 5.7 8.14995 5.7C7.24995 5.7 6.44996 5.7 5.54996 5.7C5.14996 5.7 4.84995 5.5 4.74995 5.2C4.54995 4.9 4.64995 4.6 4.84995 4.3C5.04994 4.1 5.24996 3.9 5.54996 3.9C7.24996 3.9 9.04995 3.9 10.75 3.9H10.8499C11.8499 3.9 12.8499 3.9 13.8499 3.9C13.9499 3.9 14.05 3.9 14.05 4C16.45 5.4 18.8499 6.7 21.1499 8.1C21.1499 8.1 21.15 8.1 21.25 8.2C21.05 8.3 20.85 8.4 20.75 8.5C18.55 9.7 16.3499 11.1 14.1499 12.3C14.0499 12.3 13.9499 12.4 13.8499 12.3ZM22.05 17.8C21.85 17.9 21.75 18.1 21.55 18.2C19.35 19.4 17.15 20.8 14.95 22C14.85 22.1 14.85 22.1 14.75 22.1C14.75 22 14.75 22 14.75 21.9C14.75 19.2 14.75 16.5 14.75 13.9C14.75 13.7 14.85 13.7 14.95 13.6C17.25 12.3 19.5499 10.9 21.8499 9.6C21.9499 9.5 22.05 9.5 22.05 9.5C22.05 12.3 22.05 15 22.05 17.8Z" fill="#F27E00"></path>
            <path d="M35.25 3.3H42.8499L42.2499 6.2H37.8499L36.95 10.4H40.65L40.05 13.3H36.45L35.05 20.6H31.75L35.25 3.3Z" fill="#F27E00"></path>
            <path d="M45.55 3.3H50.15C52.85 3.3 53.95 4.7 53.95 6.6C53.95 7.2 53.8499 7.9 53.7499 8.6C53.1499 11.6 52.35 12.5 50.95 13.2L51.8499 20.7H48.55L47.8499 13.8H46.65L45.2499 20.7H41.95L45.55 3.3ZM48.7499 11C49.9499 11 50.35 9.7 50.55 8.6C50.65 8.2 50.65 7.8 50.65 7.4C50.65 6.7 50.45 6.2 49.55 6.2H48.2499L47.2499 11H48.7499Z" fill="#F27E00"></path>
            <path d="M58.2499 3.3H65.8499L65.2499 6.2H60.8499L59.95 10.4H63.65L63.05 13.3H59.45L58.55 17.7H62.95L62.3499 20.6H54.7499L58.2499 3.3Z" fill="#F27E00"></path>
            <path d="M68.8499 3.3H76.45L75.8499 6.2H71.45L70.55 10.4H74.2499L73.65 13.3H69.95L69.05 17.7H73.45L72.8499 20.6H65.2499L68.8499 3.3Z" fill="#F27E00"></path>
            <path d="M84.15 16C84.05 16.5 84.05 16.9 84.05 17.2C84.05 17.6 84.1499 17.9 84.3499 18.2C84.5499 18.5 84.95 18.6 85.45 18.6C85.95 18.6 86.4499 18.4 86.7499 18.1C87.2499 17.7 87.35 17.2 87.65 16.1C87.75 15.5 87.8499 15 87.8499 14.6C87.8499 13.9 87.45 13.5 86.65 13.1L85.05 12.3C83.75 11.6 83.2499 10.8 83.2499 9.6C83.2499 9 83.25 8.4 83.45 7.7C83.75 6.2 84.25 5.1 84.95 4.4C85.75 3.6 86.65 3.2 88.15 3.2C89.65 3.2 90.35 3.6 91.05 4.3C91.45 4.7 91.7499 5.5 91.7499 6.4C91.7499 6.9 91.75 7.5 91.55 8.2H88.95C89.0499 7.6 89.05 7.3 89.05 6.9C89.05 6.5 88.9499 6.2 88.7499 5.9C88.6499 5.7 88.3499 5.5 87.8499 5.5C87.3499 5.5 87.0499 5.6 86.7499 5.9C86.3499 6.3 86.1499 6.9 85.95 7.7C85.85 8.1 85.8499 8.5 85.8499 8.9C85.8499 9.6 86.1499 10 86.8499 10.3L88.2499 11C89.9499 11.8 90.45 12.6 90.45 14C90.45 14.7 90.3499 15.4 90.2499 16.3C89.8499 18.3 89.45 19.2 88.65 19.9C87.85 20.7 86.8499 21 85.2499 21C83.7499 21 82.9499 20.7 82.2499 20C81.7499 19.5 81.45 18.8 81.45 17.9C81.45 17.4 81.55 16.8 81.65 16.1H84.15V16Z" fill="#F27E00"></path>
            <path d="M95.7499 3.3H98.3499L96.8499 10.7H100.05L101.55 3.3H104.15L100.65 20.7H98.05L99.55 13H96.45L94.95 20.7H92.3499L95.7499 3.3Z" fill="#F27E00"></path>
            <path d="M107.85 3.3H110.45L106.95 20.7H104.45L107.85 3.3Z" fill="#F27E00"></path>
            <path d="M114.15 3.3H118.05C119.45 3.3 120.25 3.6 120.85 4.2C121.45 4.8 121.65 5.6 121.65 6.6C121.65 7.2 121.65 7.8 121.45 8.6C120.55 12.9 118.95 13.9 116.35 13.9H114.55L113.25 20.7H110.65L114.15 3.3ZM116.55 11.6C117.85 11.6 118.55 10.8 118.95 8.6C119.05 8.1 119.15 7.5 119.15 7.1C119.15 6.1 118.75 5.6 117.65 5.6H116.35L115.15 11.6H116.55Z" fill="#F27E00"></path>
            <path d="M125.75 3.3H129.65C131.05 3.3 131.85 3.6 132.45 4.2C133.05 4.8 133.25 5.6 133.25 6.6C133.25 7.2 133.25 7.8 133.05 8.6C132.15 12.9 130.55 13.9 127.95 13.9H126.15L124.85 20.7H122.25L125.75 3.3ZM128.15 11.6C129.45 11.6 130.15 10.8 130.55 8.6C130.65 8.1 130.75 7.5 130.75 7.1C130.75 6.1 130.35 5.6 129.25 5.6H127.95L126.75 11.6H128.15Z" fill="#F27E00"></path>
            <path d="M137.35 3.3H139.95L136.45 20.7H133.85L137.35 3.3Z" fill="#F27E00"></path>
            <path d="M143.65 3.3H145.85L147.65 13.8L149.65 3.3H152.25L148.75 20.7H146.65L144.85 10.2L142.75 20.7H140.15L143.65 3.3Z" fill="#F27E00"></path>
            <path d="M161.45 16.1C160.75 19.4 159.25 20.9 156.45 20.9C154.95 20.9 154.05 20.4 153.45 19.5C153.05 18.8 152.85 18.2 152.85 17.1C152.85 16 153.15 14.4 153.55 12C154.65 6.7 155.15 5.4 156.25 4.3C157.05 3.5 158.05 3.1 159.45 3.1C160.75 3.1 161.75 3.5 162.35 4.2C162.85 4.8 163.05 5.7 163.05 6.6C163.05 7 163.05 7.3 162.95 7.7H160.35C160.45 7.3 160.45 7 160.45 6.7C160.45 5.9 160.15 5.3 159.25 5.3C158.75 5.3 158.35 5.5 158.05 5.8C157.35 6.5 157.05 7.6 156.15 11.9C155.75 14.1 155.45 15.9 155.45 17C155.45 18 155.85 18.5 156.65 18.5C157.75 18.5 158.45 17.6 158.75 16.2L159.35 13.3H157.75L158.15 11.2H162.35L161.45 16.1Z" fill="#F27E00"></path>
          </svg>
`;

        if (hasAssembly) {
          iconsHTML += `
  <svg width="190" height="26" viewBox="0 0 190 26" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M39.95 21.7V18.9H36.05L34.95 21.7H31.35L39.15 4.39998H42.45L43.25 21.7H39.95ZM39.95 9.19998L37.25 16.1H39.95V9.19998Z" fill="#064D5E"></path>
              <path d="M48.75 16.8C48.65 17.2 48.65 17.6 48.65 17.8C48.65 18.2 48.75 18.5 48.85 18.7C49.05 18.9 49.35 19.1 49.65 19.1C50.05 19.1 50.45 19 50.75 18.7C51.15 18.3 51.25 17.9 51.45 17.2C51.55 16.7 51.55 16.2 51.55 15.9C51.55 15.2 51.15 14.8 50.45 14.5L48.75 13.7C47.45 13 46.85 12.1 46.85 10.9C46.85 10.2 46.95 9.59998 47.05 8.79998C47.25 7.59998 47.75 6.39998 48.55 5.59998C49.35 4.79998 50.35 4.29998 52.05 4.29998C53.65 4.29998 54.55 4.69998 55.35 5.39998C55.85 5.89998 56.15 6.79998 56.15 7.69998C56.15 8.19998 56.15 8.79998 56.05 9.39998H52.75C52.85 8.89998 52.85 8.59998 52.85 8.29998C52.85 7.89998 52.75 7.59998 52.65 7.49998C52.55 7.39998 52.25 7.29998 51.95 7.29998C51.55 7.29998 51.25 7.39998 51.05 7.69998C50.75 7.99998 50.55 8.49998 50.55 8.89998C50.45 9.29998 50.45 9.59998 50.45 9.99998C50.45 10.5 50.75 11 51.35 11.2L52.75 11.9C54.65 12.7 55.25 13.6 55.25 15.1C55.25 15.8 55.15 16.5 55.05 17.4C54.65 19.4 54.25 20.2 53.45 20.9C52.55 21.8 51.55 22.2 49.55 22.2C47.95 22.2 46.95 21.8 46.25 21.1C45.75 20.6 45.35 19.7 45.35 18.8C45.35 18.3 45.45 17.7 45.55 17.1H48.95V16.8H48.75Z" fill="#064D5E"></path>
              <path d="M60.15 16.8C60.05 17.2 60.05 17.6 60.05 17.8C60.05 18.2 60.15 18.5 60.25 18.7C60.45 18.9 60.75 19.1 61.05 19.1C61.45 19.1 61.85 19 62.15 18.7C62.55 18.3 62.65 17.9 62.85 17.2C62.95 16.7 62.95 16.2 62.95 15.9C62.95 15.2 62.55 14.8 61.85 14.5L60.15 13.7C58.85 13 58.25 12.1 58.25 10.9C58.25 10.2 58.35 9.59998 58.45 8.79998C58.65 7.59998 59.15 6.39998 59.95 5.59998C60.75 4.79998 61.75 4.29998 63.45 4.29998C65.05 4.29998 65.95 4.69998 66.75 5.39998C67.25 5.89998 67.55 6.79998 67.55 7.69998C67.55 8.19998 67.55 8.79998 67.45 9.39998H64.15C64.25 8.89998 64.25 8.59998 64.25 8.29998C64.25 7.89998 64.15 7.59998 64.05 7.49998C63.95 7.39998 63.65 7.29998 63.35 7.29998C63.05 7.29998 62.65 7.39998 62.45 7.69998C62.15 7.99998 61.95 8.49998 61.95 8.89998C61.95 9.29998 61.85 9.59998 61.85 9.99998C61.85 10.5 62.15 11 62.75 11.2L64.15 11.9C66.05 12.7 66.65 13.6 66.65 15.1C66.65 15.8 66.55 16.5 66.45 17.4C66.05 19.4 65.65 20.2 64.85 20.9C63.95 21.8 62.95 22.2 60.95 22.2C59.35 22.2 58.35 21.8 57.65 21.1C57.15 20.6 56.75 19.7 56.75 18.8C56.75 18.3 56.85 17.7 56.95 17.1H60.35V16.8H60.15Z" fill="#064D5E"></path>
              <path d="M71.35 4.39998H79.05L78.45 7.29998H74.05L73.25 11.5H76.95L76.35 14.4H72.65L71.85 18.8H76.25L75.65 21.7H67.95L71.35 4.39998Z" fill="#064D5E"></path>
              <path d="M81.55 4.39998H84.45L85.75 12.8L90.55 4.39998H93.45L89.95 21.7H86.65L88.55 12.6L85.55 18.5H83.85L83.35 12.6L81.45 21.7H78.05L81.55 4.39998Z" fill="#064D5E"></path>
              <path d="M96.45 4.39998H100.95C102.55 4.39998 103.45 4.69998 104.15 5.59998C104.55 6.19998 104.85 6.89998 104.85 7.79998C104.85 8.19998 104.75 8.69998 104.65 9.39998C104.45 10.5 104.15 11.4 103.35 12.1C103.15 12.3 102.85 12.5 102.45 12.7C102.75 12.8 102.95 13.1 103.15 13.3C103.45 13.7 103.55 14.2 103.55 14.9C103.55 15.4 103.45 16 103.35 16.8C102.95 19 102.35 20.1 101.55 20.9C100.65 21.7 99.65 21.9 97.85 21.9H93.05L96.45 4.39998ZM97.85 18.8C99.35 18.8 99.65 18.1 100.05 16.5C100.15 16 100.15 15.7 100.15 15.3C100.15 14.4 99.75 14 98.85 14H97.75L96.75 18.7L97.85 18.8ZM99.25 11.3C100.55 11.3 101.05 10.7 101.35 9.39998C101.45 8.99998 101.45 8.69998 101.45 8.49998C101.45 7.79998 101.25 7.39998 100.15 7.39998H99.15L98.35 11.3H99.25Z" fill="#064D5E"></path>
              <path d="M108.65 4.39998H111.95L109.05 18.8H113.45L112.85 21.7H105.15L108.65 4.39998Z" fill="#064D5E"></path>
              <path d="M117.15 15.1L116.05 4.39998H119.45L119.65 10.9L122.35 4.39998H125.95L120.55 15.3L119.15 21.7H115.85L117.15 15.1Z" fill="#064D5E"></path>
              <path d="M132.85 5.39998C133.65 4.59998 134.75 4.19998 136.15 4.19998C137.75 4.19998 138.65 4.69998 139.25 5.59998C139.65 6.29998 139.85 6.89998 139.85 7.89998C139.85 8.99998 139.65 10.6 139.05 13C137.95 18.3 137.45 19.6 136.35 20.7C135.55 21.5 134.55 21.9 133.15 21.9C131.55 21.9 130.65 21.4 130.05 20.5C129.65 19.8 129.55 19.2 129.55 18.2C129.55 17.1 129.75 15.5 130.25 13.1C131.25 7.79998 131.65 6.49998 132.85 5.39998ZM137.15 8.09998C137.15 7.19998 136.85 6.59998 135.85 6.59998C135.35 6.59998 134.95 6.79998 134.65 7.09998C133.95 7.79998 133.65 8.89998 132.75 13.1C132.35 15.3 131.95 17.1 131.95 18.1C131.95 19 132.25 19.6 133.25 19.6C133.75 19.6 134.15 19.4 134.45 19.1C135.15 18.4 135.45 17.3 136.35 13.1C136.85 10.9 137.15 9.09998 137.15 8.09998Z" fill="#064D5E"></path>
              <path d="M143.55 4.39998H147.55C148.95 4.39998 149.75 4.59998 150.35 5.29998C150.95 5.89998 151.15 6.69998 151.15 7.69998C151.15 8.29998 151.05 8.99998 150.95 9.69998C150.15 13.9 148.45 15 145.75 15H144.05L142.65 21.8H140.05L143.55 4.39998ZM145.95 12.7C147.25 12.7 147.95 11.9 148.35 9.69998C148.45 9.19998 148.55 8.59998 148.55 8.19998C148.55 7.19998 148.15 6.79998 147.05 6.79998H145.75L144.55 12.7H145.95Z" fill="#064D5E"></path>
              <path d="M156.05 6.69998H153.25L153.75 4.39998H161.95L161.45 6.69998H158.65L155.65 21.7H153.05L156.05 6.69998Z" fill="#064D5E"></path>
              <path d="M164.25 4.39998H166.85L163.35 21.7H160.75L164.25 4.39998Z" fill="#064D5E"></path>
              <path d="M170.65 5.39998C171.45 4.59998 172.55 4.19998 173.95 4.19998C175.55 4.19998 176.45 4.69998 177.05 5.59998C177.45 6.29998 177.65 6.89998 177.65 7.89998C177.65 8.99998 177.45 10.6 176.85 13C175.75 18.3 175.25 19.6 174.15 20.7C173.35 21.5 172.35 21.9 170.95 21.9C169.35 21.9 168.45 21.4 167.85 20.5C167.45 19.8 167.35 19.2 167.35 18.2C167.35 17.1 167.55 15.5 168.05 13.1C169.05 7.79998 169.55 6.49998 170.65 5.39998ZM174.95 8.09998C174.95 7.19998 174.65 6.59998 173.65 6.59998C173.15 6.59998 172.75 6.79998 172.45 7.09998C171.75 7.79998 171.45 8.89998 170.55 13.1C170.15 15.3 169.75 17.1 169.75 18.1C169.75 19 170.05 19.6 171.05 19.6C171.55 19.6 171.95 19.4 172.25 19.1C172.95 18.4 173.25 17.3 174.15 13.1C174.65 10.9 174.95 9.09998 174.95 8.09998Z" fill="#064D5E"></path>
              <path d="M181.35 4.39998H183.55L185.35 14.9L187.35 4.39998H189.95L186.45 21.7H184.45L182.65 11.2L180.55 21.7H177.95L181.35 4.39998Z" fill="#064D5E"></path>
              <path d="M6.74999 24.1C6.84999 23.9 6.94999 23.6 7.04999 23.1C6.34999 23 5.64999 22.9 4.94999 22.9C5.54999 23.4 6.14999 23.8 6.74999 24.1Z" fill="#064D5E"></path>
              <path d="M12.95 0.0999756C11.45 0.0999756 10.15 0.299976 8.84999 0.799976C3.74999 2.49998 0.0499878 7.29998 0.0499878 12.9C0.0499878 15.5 0.849988 17.9 2.14999 19.8C2.34999 19.7 2.74999 19.7 3.14999 19.7C4.84999 19.8 6.54999 20 8.24999 20.2C8.44999 19.6 8.64999 19 8.94999 18.4C8.04999 18.3 7.24999 18.3 6.14999 18.2C6.94999 18.7 7.64999 19 8.24999 19.4C8.04999 19.5 8.04999 19.5 7.94999 19.6C6.74999 19.1 5.54999 18.4 4.24999 17.9C3.24999 17.7 2.74999 17.1 2.94999 16.2C3.04999 15.3 3.74999 14.8 4.74999 14.9C6.54999 15 8.14999 15.3 9.94999 15.3C10.15 14.8 10.35 14.3 10.55 13.7C9.44999 13.2 6.04999 13.1 7.24999 11.3C7.34999 11.1 7.34999 10.9 7.44999 10.6C6.24999 10.1 5.04999 9.69998 3.84999 9.19998C3.04999 8.89998 2.24999 8.69998 2.64999 7.59998C3.04999 6.19998 3.34999 5.99998 4.54999 6.09998C4.94999 5.69998 5.24999 4.79998 5.64999 4.99998C6.14999 5.19998 5.84999 5.99998 6.04999 6.69998C8.54999 7.59998 9.24999 7.89998 12.05 8.79998C12.35 8.09998 14.35 5.09998 15.55 5.09998C15.75 5.19998 15.95 5.19998 16.15 5.29998C17.25 6.39998 16.75 9.59998 16.55 10.4C19.05 11.3 19.95 11.7 22.25 12.6C22.75 12.2 23.05 11.3 23.55 11.6C23.95 11.8 23.65 12.6 23.65 13.2C24.15 13.4 24.75 13.9 24.35 14.8C23.85 16.2 23.55 16.4 22.05 15.9C20.85 15.5 19.65 14.9 18.45 14.7C18.25 15.1 18.15 15.5 17.95 16C18.05 16.1 18.05 16.1 18.05 16.2C19.25 16.5 19.85 17.1 19.65 18C19.55 18.8 18.75 19.3 17.55 19.2C15.95 19.1 14.25 18.9 12.55 18.7C12.35 19.3 12.15 19.8 11.85 20.5C12.55 20.6 13.15 20.6 13.85 20.7C13.85 20.6 13.85 20.6 13.95 20.5C13.55 20.3 13.15 20 12.65 19.8C12.75 19.7 12.75 19.6 12.75 19.4C13.95 19.9 15.15 20.5 16.45 20.8C17.45 21 17.95 21.7 17.85 22.5C17.75 23.3 17.05 23.8 16.05 23.8C14.75 23.7 13.55 23.6 12.35 23.5C10.65 23.3 10.65 23.3 10.15 25.1C10.85 25.2 11.45 25.2 12.35 25.3C11.75 24.9 11.35 24.6 10.85 24.3C10.95 24.2 10.95 24.2 10.95 24.1C12.05 24.5 13.15 24.9 14.35 25.4C15.05 25.3 15.85 25.2 16.45 25C21.85 23.4 25.75 18.5 25.75 12.7C25.95 5.79998 20.15 0.0999756 12.95 0.0999756Z" fill="#064D5E"></path>
            </svg>
`;
        }

        iconsHTML += `
<svg width="191" height="26" viewBox="0 0 191 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M39.35 20.85V18.05H35.55L34.45 20.85H30.95L38.65 3.55005H41.85L42.65 20.85H39.35ZM39.35 8.35005L36.75 15.15H39.35V8.35005Z" fill="#185AA5"></path>
            <path d="M47.45 3.55005H51.95C53.75 3.55005 54.65 4.15005 55.25 5.25005C55.65 5.95005 55.85 6.75005 55.85 7.75005C55.85 8.55005 55.55 10.25 55.15 12.15C54.25 16.4501 53.85 18.25 52.65 19.35C51.65 20.35 50.45 20.85 48.75 20.85H43.95L47.45 3.55005ZM49.05 17.95C49.75 17.95 50.25 17.65 50.55 17.15C51.05 16.35 51.55 14.05 51.95 12.15C52.25 10.55 52.65 8.75005 52.65 7.75005C52.65 7.35005 52.55 7.05005 52.35 6.85005C52.15 6.65005 51.85 6.55005 51.45 6.55005H50.15L47.85 18.05H49.05V17.95Z" fill="#185AA5"></path>
            <path d="M58.95 3.55005H63.45C65.25 3.55005 66.15 4.15005 66.75 5.25005C67.15 5.95005 67.35 6.75005 67.35 7.75005C67.35 8.55005 67.05 10.25 66.65 12.15C65.75 16.4501 65.35 18.25 64.15 19.35C63.15 20.35 61.95 20.85 60.25 20.85H55.45L58.95 3.55005ZM60.55 17.95C61.25 17.95 61.75 17.65 62.05 17.15C62.55 16.35 63.05 14.05 63.45 12.15C63.75 10.55 64.15 8.75005 64.15 7.75005C64.15 7.35005 64.05 7.05005 63.85 6.85005C63.65 6.65005 63.35 6.55005 62.95 6.55005H61.65L59.35 18.05H60.55V17.95Z" fill="#185AA5"></path>
            <path d="M70.95 4.75005C71.85 3.85005 72.95 3.35005 74.55 3.35005C76.35 3.35005 77.45 3.95005 78.05 5.05005C78.45 5.85005 78.65 6.45005 78.65 7.55005C78.65 8.65005 78.45 10.25 77.95 12.25C76.95 17.25 76.45 18.65 75.25 19.75C74.35 20.65 73.25 21.15 71.65 21.15C69.85 21.15 68.75 20.55 68.15 19.45C67.75 18.65 67.55 18.05 67.55 16.95C67.55 15.85 67.75 14.25 68.25 12.25C69.35 7.25005 69.85 5.85005 70.95 4.75005ZM70.95 16.85C70.95 17.65 71.25 18.15 72.05 18.15C72.45 18.15 72.85 17.95 73.05 17.65C73.65 17.05 73.95 15.85 74.75 12.15C75.05 10.45 75.45 8.55005 75.45 7.55005C75.45 6.75005 75.15 6.25005 74.35 6.25005C73.95 6.25005 73.55 6.45005 73.35 6.75005C72.75 7.35005 72.45 8.55005 71.65 12.25C71.25 13.95 70.95 15.75 70.95 16.85Z" fill="#185AA5"></path>
            <path d="M81.75 3.55005H84.45L86.25 12.35L87.85 3.55005H91.15L87.65 20.85H85.15L83.35 12.05L81.65 20.85H78.35L81.75 3.55005Z" fill="#185AA5"></path>
            <path d="M93.75 15.95C93.65 16.35 93.65 16.75 93.65 16.95C93.65 17.35 93.75 17.65 93.95 17.85C94.15 18.05 94.45 18.15 94.85 18.15C95.25 18.15 95.65 18.05 95.95 17.75C96.25 17.4501 96.45 16.9501 96.65 16.25C96.75 15.75 96.75 15.25 96.75 14.95C96.75 14.25 96.35 13.85 95.65 13.55L93.95 12.75C92.65 12.15 92.05 11.15 92.05 9.95005C92.05 9.25005 92.15 8.65005 92.25 7.85005C92.45 6.65005 92.95 5.45005 93.75 4.65005C94.55 3.85005 95.55 3.35005 97.25 3.35005C98.85 3.35005 99.75 3.75005 100.45 4.45005C100.95 4.95005 101.25 5.85005 101.25 6.65005C101.25 7.15005 101.25 7.75005 101.15 8.45005H97.85C97.95 7.95005 97.95 7.65005 97.95 7.35005C97.95 6.95005 97.85 6.65005 97.75 6.45005C97.65 6.35005 97.45 6.15005 97.05 6.15005C96.65 6.15005 96.35 6.25005 96.15 6.55005C95.85 6.85005 95.65 7.45005 95.65 7.75005C95.55 8.15005 95.55 8.45005 95.55 8.85005C95.55 9.45005 95.85 9.75005 96.45 10.05L97.85 10.75C99.65 11.65 100.35 12.45 100.35 13.95C100.35 14.65 100.35 15.45 100.15 16.25C99.75 18.15 99.35 19.05 98.65 19.75C97.75 20.65 96.75 21.05 94.85 21.05C93.25 21.05 92.25 20.65 91.55 19.95C90.95 19.35 90.65 18.55 90.65 17.65C90.65 17.15 90.65 16.65 90.85 15.85H93.75V15.95Z" fill="#185AA5"></path>
            <path d="M111.85 20.85V17.35H108.05L106.65 20.85H103.95L111.45 3.55005H113.95L114.45 20.85H111.85ZM111.85 7.55005L108.95 15.05H111.85V7.55005Z" fill="#185AA5"></path>
            <path d="M117.35 3.55005H120.15L119.65 16.75L124.45 3.55005H127.25L120.15 20.85H117.45L117.35 3.55005Z" fill="#185AA5"></path>
            <path d="M131.45 20.85V17.35H127.65L126.25 20.85H123.55L131.05 3.55005H133.55L134.05 20.85H131.45ZM131.55 7.55005L128.65 15.05H131.55V7.55005Z" fill="#185AA5"></path>
            <path d="M138.95 3.55005H141.55L138.05 20.85H135.45L138.95 3.55005Z" fill="#185AA5"></path>
            <path d="M144.25 3.55005H146.85L143.85 18.55H148.15L147.65 20.85H140.75L144.25 3.55005Z" fill="#185AA5"></path>
            <path d="M156.95 20.85V17.35H153.15L151.75 20.85H149.05L156.55 3.55005H159.05L159.55 20.85H156.95ZM156.95 7.55005L154.05 15.05H156.95V7.55005Z" fill="#185AA5"></path>
            <path d="M164.45 3.55005H168.25C169.65 3.55005 170.55 3.85005 171.15 4.55005C171.55 5.15005 171.75 5.85005 171.75 6.65005C171.75 7.05005 171.75 7.55005 171.55 8.25005C171.25 9.55005 171.05 10.45 170.15 11.15C169.85 11.45 169.55 11.65 169.15 11.75C169.45 11.85 169.75 12.15 169.95 12.35C170.35 12.85 170.45 13.35 170.45 13.95C170.45 14.45 170.45 15.05 170.25 15.75C169.85 17.95 169.35 18.9501 168.45 19.75C167.55 20.55 166.65 20.75 164.95 20.75H160.85L164.45 3.55005ZM165.25 18.55C166.75 18.55 167.35 17.85 167.75 15.85C167.85 15.35 167.85 14.95 167.85 14.55C167.85 13.45 167.35 12.95 166.25 12.95H165.15L164.05 18.55H165.25ZM166.65 10.75C168.05 10.75 168.65 10.05 169.05 8.35005C169.15 7.95005 169.15 7.55005 169.15 7.25005C169.15 6.35005 168.85 5.85005 167.65 5.85005H166.45L165.45 10.75H166.65Z" fill="#185AA5"></path>
            <path d="M175.25 3.55005H177.85L174.85 18.55H179.15L178.65 20.85H171.75L175.25 3.55005Z" fill="#185AA5"></path>
            <path d="M184.05 3.55005H190.95L190.45 5.85005H186.15L185.15 10.95H188.85L188.35 13.25H184.65L183.65 18.55H187.95L187.45 20.85H180.55L184.05 3.55005Z" fill="#185AA5"></path>
            <path d="M12.75 25.45C19.764 25.45 25.45 19.7641 25.45 12.75C25.45 5.73603 19.764 0.0500488 12.75 0.0500488C5.73598 0.0500488 0.0499878 5.73603 0.0499878 12.75C0.0499878 19.7641 5.73598 25.45 12.75 25.45Z" fill="#185AA5"></path>
            <path d="M12.35 7.55005C12.35 11.65 12.35 15.75 12.35 19.75C11.85 19.75 11.35 19.65 10.85 19.65C9.25001 19.45 7.54998 19.25 5.94998 19.15C5.34998 19.05 4.85001 18.65 4.85001 18.05C4.85001 16.15 4.85001 14.25 4.85001 12.35C5.05001 12.35 5.15 12.35 5.25 12.35C6.25 12.35 7.35001 12.35 8.35001 12.35C9.25001 12.35 9.94998 11.95 10.45 11.15C11.05 9.95005 11.65 8.75005 12.35 7.55005Z" fill="white"></path>
            <path d="M13.25 7.55005C13.35 7.75005 13.45 7.85005 13.55 7.95005C14.15 8.95005 14.65 10.05 15.25 11.05C15.65 11.85 16.35 12.25 17.25 12.25C18.25 12.25 19.35 12.25 20.35 12.25C20.45 12.25 20.65 12.25 20.85 12.25C20.85 12.45 20.85 12.55 20.85 12.65C20.85 14.35 20.85 15.95 20.85 17.65C20.85 18.45 20.45 18.95 19.55 19.05C17.65 19.25 15.75 19.45 13.85 19.65C13.25 19.65 13.25 19.65 13.25 19.15C13.25 15.45 13.25 11.65 13.25 7.95005C13.25 7.95005 13.25 7.85005 13.25 7.55005Z" fill="white"></path>
            <path d="M20.15 11.45C19.15 11.45 18.15 11.45 17.15 11.45C16.55 11.45 16.25 11.25 15.95 10.75C15.15 9.35005 14.35 7.85005 13.55 6.35005C13.45 6.25005 13.45 6.05005 13.55 5.95005C13.55 5.85005 13.85 5.85005 13.95 5.85005C16.05 6.05005 18.05 6.35005 20.05 6.55005C20.55 6.55005 20.95 6.85005 21.25 7.25005C22.05 8.45005 22.75 9.55005 23.45 10.75C23.55 10.85 23.65 11.15 23.55 11.25C23.55 11.35 23.25 11.45 23.05 11.45C22.15 11.45 21.15 11.45 20.15 11.45Z" fill="white"></path>
            <path d="M5.44998 11.45C4.44998 11.45 3.44998 11.45 2.44998 11.45C1.84998 11.45 1.74999 11.25 2.04999 10.85C2.84999 9.65005 3.55001 8.55005 4.35001 7.35005C4.65001 6.95005 4.94998 6.65005 5.44998 6.65005C7.54998 6.45005 9.54999 6.15005 11.65 5.95005C11.75 5.95005 11.95 5.95005 12.05 6.05005C12.05 6.15005 12.05 6.35005 12.05 6.45005C11.25 7.95005 10.45 9.45005 9.64999 10.95C9.44999 11.35 9.04999 11.55 8.54999 11.55C7.44999 11.45 6.44998 11.45 5.44998 11.45Z" fill="white"></path>
          </svg>
`;

        // Update icons wrapper
        const iconsWrapper = root.querySelector('.lift-kit__icons');
        if (iconsWrapper) {
          iconsWrapper.innerHTML = iconsHTML;
        }

        const variant =
          data.variants.find((v) => String(v.id) === variantId) || data.variants[0];
          const price = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(variant.price / 100);        const compare = variant.compare_at_price
          ? (variant.compare_at_price / 100).toFixed(2)
          : null;

        const productUrl = `/products/${handle}?variant=${variantId}`;

        infoBox.innerHTML = `
          <div class="kit-info-box text-center max-w-md mx-auto mt-4 md:mt-6">
            <p class="price price_kit">
              ${
                compare
                  ? `<span class="line-through text-gray-400 text-lg mr-2">$${compare}</span>`
                  : ''
              }
              ${price}
            </p>

            <!-- CTA -->
            <div class="cta-wrap w-full
              p-[10px] flex justify-center items-center w-[500px]
              bg-[var(--shopify-editor-setting-color_product_button_bg)]
              text-white font-extrabold text-[25px]
              rounded-[4px] leading-[1em] tracking-[1px]
              font-dinprocondensed
              pt-[2px]
              mt-2
            " style="padding-top:3px">
              <a href="${productUrl}" class="details_product
                p-[10px] flex justify-center items-center w-[500px]
                bg-[var(--shopify-editor-setting-color_product_button_bg)]
                text-white font-extrabold text-[25px]
                rounded-[4px] leading-[1em] tracking-[1px]
                font-dinprocondensed
              ">DETAILS</a>
            </div>
          </div>
        `;
        infoBox.classList.remove('loading');
      } catch (err) {
        console.error('Product fetch failed', err);
      }
      renderActiveVariantImage();

    });
  });
}





function renderActiveVariantImage() {
  const activeLift = document.querySelector('.lift-btn.active');
const activeLoad = document.querySelector('.load-btn.active');

// ⛔ ako smo u prijelazu lift → load, NE DIRAJ sliku
if (activeLift && !activeLoad) {
  return;
}
  const imgFallback = document.querySelector('[data-kit-image]');
  const variantLinkTarget = document.querySelector('[data-variant-details]');

  if (!imgFallback || !variantLinkTarget) return;

  if (activeLoad?.dataset.variantId) {
    variantLinkTarget.innerHTML = '';
    imgFallback.style.display = 'none';
  
    renderVariantDetails(activeLoad.dataset.variantId);
  
    if (activeLoad.dataset.imgUrl) {
      imgFallback.src = activeLoad.dataset.imgUrl;
      imgFallback.style.display = 'block';
    }
    return;
  }
  

  // 2️⃣ FALLBACK: SHOCK SLIKA
  const activeShock = document.querySelector('.shock-btn.active');
  if (!activeShock) return;

  const shockName = normalizeShock(activeShock.dataset.shock);

const shockImageLink = [...document.querySelectorAll('.kit-variant-image-link')]
  .find(el => normalizeShock(el.dataset.shock) === shockName);

  if (!shockImageLink) return;

  const img = shockImageLink.querySelector('img');
  if (!img) return;

  


}





/* ---------- Auto-select prvog shocka (koji je već sortiran po Order_*) ---------- */

function autoSelectFirst(root) {
  const firstShockBtn = root.querySelector('.shock-btn');
  if (firstShockBtn) {
    firstShockBtn.click();
  }
}

/* ---------- Dropdown za kit-builder-links ---------- */

document.addEventListener('DOMContentLoaded', initKitBuilder);

document.addEventListener('DOMContentLoaded', () => {
  const placeholders = document.querySelectorAll('.kit-builder-links__placeholder');

  placeholders.forEach((el) => {
    const options = el.nextElementSibling;

    el.addEventListener('click', (e) => {
      e.stopPropagation();
      el.classList.toggle('active');
      options.classList.toggle('hidden');
    });
  });

  // Close dropdown kad klikneš vani
  document.addEventListener('click', (e) => {
    document
      .querySelectorAll('.kit-builder-links__placeholder.active')
      .forEach((openEl) => {
        if (!openEl.contains(e.target)) {
          openEl.classList.remove('active');
          openEl.nextElementSibling.classList.add('hidden');
        }
      });
  });
});



document.addEventListener('click', function (event) {
  const wrapper = event.target.closest('.kit-image-wrapper');
  if (!wrapper) return;

  const detailsProduct = document.querySelector('.details_product');
  if (detailsProduct) {
    detailsProduct.click();
  }
});
